import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Download, Share, CheckCircle2, Smartphone, ArrowRight } from "lucide-react";

/**
 * /kur — girişsiz açılan kurulum sayfası. Masalardaki A4 ritüel kağıdının
 * 1. adımı buraya QR ile gelir. Amaç: uygulama ANA EKRANA eklendikten sonra
 * giriş yapılması (iOS'ta kurulu uygulama Safari ile oturumu paylaşmaz;
 * önce tarayıcıda giriş yaptırmak öğrenciye ikinci bir giriş yaşatır).
 */

type InstallPromptEvent = Event & {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
};

const isStandaloneMode = () =>
  typeof window !== "undefined" &&
  (window.matchMedia?.("(display-mode: standalone)")?.matches ||
    (window.navigator as unknown as { standalone?: boolean })?.standalone === true);

const isIos = () => /iphone|ipad|ipod/i.test(typeof navigator === "undefined" ? "" : navigator.userAgent);

export default function InstallAppPage() {
  const navigate = useNavigate();
  const [installed, setInstalled] = useState(isStandaloneMode);
  const [justInstalled, setJustInstalled] = useState(false);
  const [canPrompt, setCanPrompt] = useState(() => Boolean(window.__deferredInstallPrompt));

  useEffect(() => {
    const handleReady = () => setCanPrompt(true);
    const handleInstalled = () => {
      setJustInstalled(true);
      setCanPrompt(false);
    };
    window.addEventListener("installPromptReady", handleReady);
    window.addEventListener("appinstalled", handleInstalled);
    return () => {
      window.removeEventListener("installPromptReady", handleReady);
      window.removeEventListener("appinstalled", handleInstalled);
    };
  }, []);

  const handleInstall = async () => {
    const deferred = window.__deferredInstallPrompt;
    if (!deferred) return;
    window.__deferredInstallPrompt = null;
    setCanPrompt(false);
    await deferred.prompt();
    await (deferred as InstallPromptEvent).userChoice.catch(() => undefined);
  };

  const ios = isIos();

  return (
    <div className="min-h-dvh bg-gradient-to-br from-slate-950 via-slate-900 to-blue-950 flex items-center justify-center p-5">
      <div className="w-full max-w-sm flex flex-col items-center gap-6">
        <img src="/logo.svg" alt="YES" className="h-14" />

        {installed ? (
          // Zaten kurulu uygulamanın İÇİNDEN açıldı
          <div className="w-full bg-white/5 border border-white/10 rounded-2xl p-6 flex flex-col items-center gap-4 text-center">
            <CheckCircle2 className="w-14 h-14 text-green-400" />
            <div>
              <p className="text-lg font-bold text-white">Uygulama kurulu, hazırsın!</p>
              <p className="text-sm text-white/60 mt-1">Bu adımı kağıtta işaretle ve devam et.</p>
            </div>
            <button
              onClick={() => navigate("/", { replace: true })}
              className="w-full py-3 rounded-xl bg-blue-600 hover:bg-blue-500 text-white text-sm font-semibold transition-colors flex items-center justify-center gap-2"
            >
              Giriş yap ve başla <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        ) : justInstalled ? (
          // Kurulum az önce tamamlandı — artık ana ekrandaki simgeden devam etmeli
          <div className="w-full bg-white/5 border border-white/10 rounded-2xl p-6 flex flex-col items-center gap-4 text-center">
            <CheckCircle2 className="w-14 h-14 text-green-400" />
            <div>
              <p className="text-lg font-bold text-white">Kuruldu!</p>
              <p className="text-sm text-white/60 mt-1">
                Bu sekmeyi kapat, <strong className="text-white">ana ekranındaki YES simgesinden</strong> aç ve giriş yap.
              </p>
            </div>
          </div>
        ) : (
          <div className="w-full bg-white/5 border border-white/10 rounded-2xl p-6 flex flex-col gap-5">
            <div className="text-center">
              <p className="text-lg font-bold text-white">YES'i ana ekranına ekle</p>
              <p className="text-sm text-white/60 mt-1">
                Her gelişinde tek dokunuşla açılır — uygulama gibi, tarayıcı karmaşası olmadan.
              </p>
            </div>

            {ios ? (
              <div className="flex flex-col gap-3">
                {[
                  { icon: Share, text: "Safari'nin altındaki Paylaş simgesine dokun." },
                  { icon: Download, text: '"Ana Ekrana Ekle" seçeneğini bul, dokun.' },
                  { icon: Smartphone, text: '"Ekle" de — YES simgesi ana ekranında.' },
                ].map((step, i) => (
                  <div key={i} className="flex items-center gap-3 bg-white/5 rounded-xl px-4 py-3">
                    <span className="w-7 h-7 rounded-lg bg-blue-500/25 flex items-center justify-center shrink-0 text-blue-300 text-sm font-bold">
                      {i + 1}
                    </span>
                    <step.icon className="w-5 h-5 text-blue-300 shrink-0" />
                    <span className="text-sm text-white/85">{step.text}</span>
                  </div>
                ))}
                <p className="text-xs text-white/40 text-center">
                  iPhone'da kurulum yalnız Safari'den yapılabilir.
                </p>
              </div>
            ) : canPrompt ? (
              <button
                onClick={handleInstall}
                className="w-full py-3.5 rounded-xl bg-blue-600 hover:bg-blue-500 text-white text-base font-bold transition-colors flex items-center justify-center gap-2"
              >
                <Download className="w-5 h-5" /> Ana Ekrana Ekle
              </button>
            ) : (
              <p className="text-sm text-white/70 text-center bg-white/5 rounded-xl px-4 py-3">
                Tarayıcı menüsünden <strong className="text-white">"Ana ekrana ekle"</strong> ya da{" "}
                <strong className="text-white">"Uygulamayı yükle"</strong> seçeneğini kullan.
              </p>
            )}

            <button
              onClick={() => navigate("/", { replace: true })}
              className="text-xs text-white/40 hover:text-white/70 transition-colors"
            >
              Şimdilik geç, tarayıcıdan devam et
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
